import useUpdateConfig from "api/ConfigResource/useUpdateConfig";
import { Form, Formik } from "formik";
import { useState } from "react";
import {
	AiOutlineLoading3Quarters,
	AiOutlineCheck,
	AiOutlineEdit,
} from "react-icons/ai";
import * as Yup from "yup";

/**
 * @param {Object} props
 * @param {string} props.name
 * @param {string} props.label
 * @param {string} props.value
 * @param {string} props.dataType
 * @param {string} props.id
 */
const ConfigItem = (props) => {
	const { name, label, value, id, dataType } = props;

	const [isEditing, setIsEditing] = useState(false);

	const initialValues = {
		value: value || "",
	};

	const validationSchema = Yup.object().shape({
		value: Yup.string().required("Value is required"),
	});

	const { mutate: updateConfig, isLoading } = useUpdateConfig(id);

	const handleSubmit = (values) => {
		try {
			updateConfig(values);
			setIsEditing(false);
		} catch (error) {
			console.log(error);
		}
	};

	return (
		<div
			className="bg-white rounded-lg shadow border border-gray-200 px-4 py-3 flex flex-col gap-2 transition-all duration-150 hover:shadow-md hover:border-green-200 relative group min-w-[260px] max-w-md"
			style={{ minHeight: 0 }}
		>
			<div className="flex items-center justify-between mb-1">
				<div>
					<div className="text-base font-semibold text-green-900 leading-tight truncate">
						{label}
					</div>
					<div className="text-xs text-gray-500 font-medium truncate">
						{name}
					</div>
				</div>
				<div className="text-xs text-gray-500 font-medium bg-gray-100 px-2 py-1 rounded-full">
					{dataType}
				</div>
			</div>

			{isEditing ? (
				<Formik
					initialValues={initialValues}
					validationSchema={validationSchema}
					onSubmit={handleSubmit}
				>
					{({ values, setFieldValue, errors, touched }) => (
						<Form className="flex items-center gap-2 mt-1 w-full">
							<input
								type="text"
								name="value"
								value={values.value}
								className="flex-1 px-2 py-1.5 rounded border border-gray-300 bg-gray-50 text-green-900 text-sm focus:border-green-400 focus:ring-1 focus:ring-green-100 focus:outline-none transition-all h-9 shadow-sm"
								onChange={(e) => setFieldValue("value", e.target.value)}
								disabled={isLoading}
								style={{ minWidth: 0 }}
							/>
							<button
								type="submit"
								disabled={isLoading}
								className="px-3 py-1.5 rounded bg-green-600 text-white hover:bg-green-700 transition-colors font-medium flex items-center gap-1 h-9 shadow-sm disabled:opacity-60 disabled:cursor-not-allowed"
								title="Save changes"
							>
								{isLoading ? (
									<AiOutlineLoading3Quarters
										size={16}
										className="animate-spin"
									/>
								) : (
									<AiOutlineCheck size={16} />
								)}
							</button>
							<button
								type="button"
								className="px-2 py-1.5 rounded bg-gray-100 text-gray-500 hover:bg-gray-200 transition-colors font-medium flex items-center gap-1 h-9 shadow-sm"
								title="Cancel"
								onClick={() => setIsEditing(false)}
							>
								Cancel
							</button>
							{errors.value && touched.value && (
								<div className="text-red-400 text-xs absolute left-0 top-full mt-0.5 ml-1">
									{errors.value}
								</div>
							)}
						</Form>
					)}
				</Formik>
			) : (
				<div className="flex items-center justify-between mt-1 w-full">
					<span className="px-2 py-1.5 rounded bg-gray-50 text-green-900 text-sm font-medium h-9 flex items-center max-w-[70%] truncate border border-gray-100">
						{value || <span className="text-gray-400">No value</span>}
					</span>
					<button
						className="p-1.5 rounded bg-green-50 hover:bg-green-100 transition-colors flex items-center justify-center"
						title="Edit"
						type="button"
						onClick={() => setIsEditing(true)}
					>
						<AiOutlineEdit
							size={18}
							className="text-green-600"
						/>
					</button>
				</div>
			)}
		</div>
	);
};

export default ConfigItem;
